<script lang="ts" setup>
import {
  Banner,
  BannerVariant,
  Button,
  ButtonGroup,
  Form,
  Heading,
  Listbox,
  ListboxOption,
} from '@app/customer/Components';
import { AppLayout } from '@app/customer/Layouts';
import { useForm } from '@inertiajs/vue3';
import { useBrandColor } from '@app/customer/Composables/useBrandColor';
import { type Types } from '@app/shared/types/generated-v2';
import { paymentMethodService } from '@app/customer/Services';
import { useI18n } from 'vue-i18n';
import { ref } from 'vue';

type ShowPaymentRequestPaymentMethodsViewModel = Types['App.Http.ViewModels.Customer.ShowPaymentRequestPaymentMethodsViewModel'];

const { t } = useI18n();

const props = defineProps<ShowPaymentRequestPaymentMethodsViewModel>();

const buttonColor = useBrandColor();

const { getPaymentMethodLogo } = paymentMethodService();

const form = useForm({
  payment_method: props.selectedPaymentMethod?.id ?? null,
});

const loading = ref(false);

function submitForm() {
  loading.value = true;

  form.post(route('customer.intent.payment-request.payment-method.update', { paymentRequest: props.paymentRequestId }), {
    preserveScroll: true,

    /**
     * The onFinish event can't be used here because the controller responds
     * with an Inertia::location redirect, which triggers onFinish before redirecting.
     * This briefly enables the button before navigation.
     * Instead, we use onSuccess and onError, as they don’t trigger on redirects.
     */
    onSuccess: () => loading.value = false,
    onError: () => loading.value = false,
  });
}
</script>

<template>
  <AppLayout>
    <div class="max-w-3xl mx-auto">
      <div class="flex flex-col sm:flex-row sm:space-x-4 justify-between border-b border-slate-100 pb-6">
        <Heading>
          {{ t('customer.payment-request:payment-method:title') }}
        </Heading>
      </div>

      <div
        v-if="paymentStatus"
        class="mt-8 mb-8"
      >
        <Banner :variant="BannerVariant.Warning">
          {{ t('customer.payment:unable-to-process') }}
        </Banner>
      </div>

      <Form
        class="mt-4"
        @submit="submitForm"
      >
        <Listbox>
          <template
            v-for="paymentMethod in paymentMethods"
            :key="paymentMethod.id"
          >
            <ListboxOption
              v-model="form.payment_method"
              :label="paymentMethod.name"
              :value="paymentMethod.id"
              :image-url="getPaymentMethodLogo(paymentMethod)"
              :alt="paymentMethod.name"
            />
          </template>
        </Listbox>

        <div class="mt-12 ml-auto">
          <ButtonGroup>
            <Button
              full-width
              :href="$route('customer.intent.payment-request.show', { paymentRequest: paymentRequestId })"
            >
              {{ t('customer.payment-request:payment-method:back') }}
            </Button>

            <Button
              :color="buttonColor"
              full-width
              type="submit"
              name="submit_action"
              value="payment_method"
              :disabled="form.payment_method === null || loading"
            >
              {{ t('customer.payment-request:payment-method:pay', { price }) }}
            </Button>
          </ButtonGroup>
        </div>
      </Form>
    </div>
  </AppLayout>
</template>
